import React from 'react';
import { Alert, Spin, Table, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useCurrency } from '@/contexts/CurrencyContext';
import {
  filtersQueryKey,
  getSalesAnalyticsTeam,
  type SalesAnalyticsFilterParams,
} from '@/services/salesAnalytics.service';
import { OiHorizontalBarChart } from '@/components/owner-intelligence/charts/OiCharts';
import { SalesAnalyticsToolbar } from './SalesAnalyticsToolbar';

const { Title } = Typography;

export const SalesAnalyticsTeamTab: React.FC<{
  filters: SalesAnalyticsFilterParams;
  filterSummary: string;
}> = ({ filters, filterSummary }) => {
  const { formatCurrency } = useCurrency();
  const query = useQuery({
    queryKey: ['sales-analytics', 'team', filtersQueryKey(filters)],
    queryFn: () => getSalesAnalyticsTeam(filters),
  });

  if (query.isLoading) return <Spin />;
  if (query.isError) return <Alert type="error" message={(query.error as Error).message} />;
  const d = query.data!;

  const printRows = d.teams.map((r) => ({
    team: r.team_name,
    leads: r.leads,
    opportunities: r.opportunities,
    quotations: r.quotations,
    booked_value: formatCurrency(r.booked_value),
    invoiced: formatCurrency(r.invoiced),
    collected: formatCurrency(r.collected),
  }));

  const chartData = d.teams.map((r) => ({ name: r.team_name, value: r.invoiced }));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <Title level={5} style={{ margin: 0 }}>
          Team performance
        </Title>
        <SalesAnalyticsToolbar
          tab="team"
          tabTitle="Team Performance"
          filters={filters}
          filterSummary={filterSummary}
          printColumns={[
            { key: 'team', label: 'Team' },
            { key: 'leads', label: 'Leads', align: 'right' },
            { key: 'booked_value', label: 'Booked', align: 'right' },
            { key: 'invoiced', label: 'Invoiced', align: 'right' },
            { key: 'collected', label: 'Collected', align: 'right' },
          ]}
          printRows={printRows}
        />
      </div>

      <OiHorizontalBarChart data={chartData} dataKey="value" nameKey="name" height={220} />

      <Table
        style={{ marginTop: 16 }}
        size="small"
        rowKey="team_id"
        dataSource={d.teams}
        pagination={false}
        scroll={{ x: 900 }}
        columns={[
          { title: 'Team', dataIndex: 'team_name', fixed: 'left' },
          { title: 'Leads', dataIndex: 'leads', align: 'right' },
          { title: 'Opportunities', dataIndex: 'opportunities', align: 'right' },
          { title: 'Quotations', dataIndex: 'quotations', align: 'right' },
          { title: 'Booked orders', dataIndex: 'booked_orders', align: 'right' },
          { title: 'Booked value', dataIndex: 'booked_value', align: 'right', render: (v: number) => formatCurrency(v) },
          { title: 'Invoiced', dataIndex: 'invoiced', align: 'right', render: (v: number) => formatCurrency(v) },
          { title: 'Collected', dataIndex: 'collected', align: 'right', render: (v: number) => formatCurrency(v) },
        ]}
      />
    </div>
  );
};
